/**
 * Recipe Suggestions Widget
 * Shows recipe recommendations based on available ingredients
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { recipeAgent } from '@/services/agents';
import linkingHelper from '@/utils/linkingHelper';
import { ChefHat, Clock, Users, TrendingUp, Tag } from 'lucide-react';

interface RecipeSuggestionsWidgetProps {
  ingredients: string[];
  maxRecipes?: number;
}

const RecipeSuggestionsWidget: React.FC<RecipeSuggestionsWidgetProps> = ({
  ingredients,
  maxRecipes = 3,
}) => {
  const [recipes, setRecipes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ingredients && ingredients.length > 0) {
      setLoading(true);
      const foundRecipes = recipeAgent.getRecipesByIngredients(ingredients, maxRecipes);
      setRecipes(foundRecipes);
      setLoading(false);
    }
  }, [ingredients, maxRecipes]);

  if (loading) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <ChefHat size={20} style={styles.icon} />
          <h3 style={styles.title}>Recipe Ideas</h3>
        </div>
        <p style={styles.loading}>Finding recipes...</p>
      </div>
    );
  }

  if (recipes.length === 0) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <ChefHat size={20} style={styles.icon} />
          <h3 style={styles.title}>Recipe Ideas</h3>
        </div>
        <p style={styles.noRecipes}>
          No recipes found for these ingredients. Try browsing our full recipe collection!
        </p>
      </div>
    );
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '#2D5A27';
      case 'medium':
        return '#D4883A';
      case 'hard':
        return '#C4704F';
      default:
        return '#999';
    }
  };

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <ChefHat size={20} style={styles.icon} />
        <div>
          <h3 style={styles.title}>Recipe Ideas</h3>
          <p style={styles.subtitle}>Using these ingredients</p>
        </div>
      </div>

      <div style={styles.recipesList}>
        {recipes.map((recipe, index) => {
          const recipeUrl = linkingHelper.getRecipeUrl(recipe.id || recipe.name);
          return (
            <Link
              key={recipe.id}
              to={recipeUrl}
              style={styles.recipeCardLink}
            >
              <div style={styles.recipeCard}>
                <div style={styles.recipeHeader}>
                  <h4 style={styles.recipeName}>{recipe.name}</h4>
                  <span
                    style={{
                      ...styles.difficultyBadge,
                      backgroundColor: getDifficultyColor(recipe.difficulty),
                    }}
                  >
                    {recipe.difficulty}
                  </span>
                </div>

                <p style={styles.recipeDescription}>{recipe.description}</p>

                <div style={styles.recipeStats}>
                  <div style={styles.statItem}>
                    <Clock size={14} />
                    <span>{recipe.prepTime + recipe.cookTime} min</span>
                  </div>
                  <div style={styles.statItem}>
                    <Users size={14} />
                    <span>{recipe.servings} servings</span>
                  </div>
                </div>

            {recipe.dietaryTags && recipe.dietaryTags.length > 0 && (
              <div style={styles.dietaryTags}>
                {recipe.dietaryTags.slice(0, 3).map((tag: string, i: number) => (
                  <span key={i} style={styles.dietaryTag}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {recipe.primaryProduce && recipe.primaryProduce.length > 0 && (
              <div style={styles.ingredients}>
                <strong style={styles.ingredientsLabel}>
                  <Tag size={12} style={{ marginRight: '4px' }} />
                  Main ingredients:
                </strong>
                <div style={styles.ingredientTags}>
                  {recipe.primaryProduce.map((ingredient: string, idx: number) => {
                    const ingredientUrl = linkingHelper.getProductUrl(ingredient);
                    return (
                      <Link
                        key={idx}
                        to={ingredientUrl}
                        onClick={(e) => e.stopPropagation()}
                        style={styles.ingredientTag}
                      >
                        {ingredient}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
              </div>
            </Link>
          );
        })}
      </div>

      <div style={styles.footer}>
        <TrendingUp size={14} />
        <span style={styles.footerText}>More recipes available in our collection</span>
      </div>
    </div>
  );
};

const styles = {
  widget: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '20px',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,
  icon: {
    color: '#D4883A',
  } as React.CSSProperties,
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
  } as React.CSSProperties,
  subtitle: {
    fontSize: '12px',
    color: '#999',
    margin: '4px 0 0 0',
  } as React.CSSProperties,
  loading: {
    fontSize: '14px',
    color: '#999',
    textAlign: 'center' as const,
    padding: '20px',
  } as React.CSSProperties,
  noRecipes: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center' as const,
    padding: '20px',
    backgroundColor: '#F9FAF8',
    borderRadius: '8px',
  } as React.CSSProperties,
  recipesList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    marginBottom: '16px',
  } as React.CSSProperties,
  recipeCardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'transform 0.2s',
  } as React.CSSProperties,
  recipeCard: {
    padding: '16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '12px',
    border: '1px solid #E8EDE7',
    transition: 'box-shadow 0.2s, transform 0.2s',
    cursor: 'pointer',
  } as React.CSSProperties,
  recipeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  } as React.CSSProperties,
  recipeName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0,
    flex: 1,
  } as React.CSSProperties,
  difficultyBadge: {
    fontSize: '10px',
    padding: '4px 10px',
    borderRadius: '12px',
    color: '#fff',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,
  recipeDescription: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '12px',
    lineHeight: '1.5',
  } as React.CSSProperties,
  recipeStats: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
  } as React.CSSProperties,
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#666',
  } as React.CSSProperties,
  dietaryTags: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
    marginBottom: '10px',
  } as React.CSSProperties,
  dietaryTag: {
    fontSize: '11px',
    padding: '4px 10px',
    backgroundColor: '#E8EDE7',
    color: '#2D5A27',
    borderRadius: '12px',
    fontWeight: '500',
  } as React.CSSProperties,
  ingredients: {
    fontSize: '12px',
    color: '#666',
    paddingTop: '10px',
    borderTop: '1px solid #E8EDE7',
  } as React.CSSProperties,
  ingredientsLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    color: '#1a1a1a',
    fontSize: '12px',
  } as React.CSSProperties,
  ingredientTags: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
  } as React.CSSProperties,
  ingredientTag: {
    fontSize: '11px',
    padding: '4px 10px',
    backgroundColor: '#2D5A27',
    color: '#fff',
    borderRadius: '12px',
    fontWeight: '500',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  } as React.CSSProperties,
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '12px',
    borderTop: '1px solid #E8EDE7',
  } as React.CSSProperties,
  footerText: {
    fontSize: '12px',
    color: '#999',
  } as React.CSSProperties,
};

export default RecipeSuggestionsWidget;
